import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Button, ProgressBar, Dropdown } from 'react-bootstrap';
import { FaUsers, FaHandshake, FaDollarSign, FaCalendarCheck, FaChartLine, FaTrophy, FaPlus, FaBell, FaEye, FaBuilding, FaStar, FaUserTie, FaMapMarkerAlt, FaCheckCircle, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axiosInstance from 'helpers/api';
import BrokerApiService from '../../services/brokerApiService';

interface AgentStats {
  activeTransactions: number;
  closedThisMonth: number;
  pipelineValue: number;
  commissionEarned: number;
  averageDaysToClose: number;
  goalProgress: number;
  monthlyGoal: number;
}

interface BrokerageInfo {
  brokerageName: string;
  brokerName: string;
  agentCount: number;
  state: string;
  city: string;
}

interface AgentInfo {
  name: string;
  title: string;
  licenseNumber: string;
  joinDate: string;
  rank: string;
  totalTransactions: number;
}

interface RecentActivity {
  id: string;
  type: 'document' | 'client' | 'transaction' | 'closing';
  title: string;
  description: string;
  timestamp: string;
  priority?: 'high' | 'medium' | 'low';
}

interface ActiveFile {
  id: string;
  property_address: string;
  stage: string;
  days_in_stage: number;
  next_action: string;
  client_name: string;
  type: 'buyer' | 'seller' | 'dual';
}

interface BrokerReviewItem {
  id: string;
  transaction_id: string;
  document_name: string;
  status: 'pending' | 'approved' | 'needs_revision';
  submitted_date: string;
  reviewed_date?: string;
  score?: number;
  broker_notes?: string;
  property_address: string;
}

/**
 * Helper function to extract state from address string
 */
const extractStateFromAddress = (address?: string): string | null => {
  if (!address) return null;
  
  // Look for common state abbreviations at the end of the address
  const stateMatch = address.match(/,\s*([A-Z]{2})\s*\d{5}/);
  if (stateMatch) return stateMatch[1];
  
  return null;
};

/**
 * Helper function to extract city from address string
 */
const extractCityFromAddress = (address?: string): string | null => {
  if (!address) return null;
  
  // Look for city before state and zip code
  const cityMatch = address.match(/,\s*([^,]+),\s*[A-Z]{2}\s*\d{5}/);
  if (cityMatch) return cityMatch[1].trim();
  
  // Fallback: take everything before the last comma
  const parts = address.split(',');
  if (parts.length >= 2) {
    return parts[parts.length - 2].trim();
  }
  
  return null;
};

const EnhancedAgentDashboard: React.FC = () => {
  const [agentStats, setAgentStats] = useState<AgentStats>({
    activeTransactions: 0,
    closedThisMonth: 0,
    pipelineValue: 0,
    commissionEarned: 0,
    averageDaysToClose: 0,
    goalProgress: 0,
    monthlyGoal: 0
  });

  const [brokerageInfo, setBrokerageInfo] = useState<BrokerageInfo>({
    brokerageName: 'Loading...',
    brokerName: 'Loading...',
    agentCount: 0,
    state: '',
    city: ''
  });

  const [agentInfo, setAgentInfo] = useState<AgentInfo>({
    name: 'Loading...',
    title: 'Real Estate Agent',
    licenseNumber: '',
    joinDate: '',
    rank: 'Rising Star',
    totalTransactions: 0
  });

  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [activeFiles, setActiveFiles] = useState<ActiveFile[]>([]);
  const [brokerReviews, setBrokerReviews] = useState<BrokerReviewItem[]>([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  // Helper function to format timestamps
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} minutes ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hours ago`;
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      console.log('🔍 [Dashboard] Starting to fetch dashboard data...');
      console.log('🔍 [Dashboard] Auth token:', localStorage.getItem('auth_token') ? 'Present' : 'Missing');
      
      // Get current user info from localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      console.log('🔍 [Dashboard] User from localStorage:', user);
      
      // Use the EXACT same endpoint that Broker Intelligence Hub uses
      console.log('🔍 [Dashboard] Fetching company profile via /broker/company-profile...');
      
      try {
        const profileResponse = await axiosInstance.get('/broker/company-profile');
        console.log('🔍 [Dashboard] Company profile response:', profileResponse.data);
        
        if (profileResponse.data.success && profileResponse.data.data) {
          const companyData = profileResponse.data.data;
          
          setBrokerageInfo({
            brokerageName: companyData.company_name || 'KairosIQ AI Realty, Inc.',
            brokerName: 'Michael Boykin', // Default broker name
            agentCount: 0, // Will be populated separately if needed
            state: extractStateFromAddress(companyData.company_address) || 'AZ',
            city: extractCityFromAddress(companyData.company_address) || 'Chandler'
          });
          
          console.log('🔍 [Dashboard] Company info set successfully:', {
            name: companyData.company_name,
            address: companyData.company_address,
            brokerageId: companyData.brokerage_id
          });
        } else {
          console.warn('🔍 [Dashboard] Company profile was not successful, using fallback');
          // Use the exact same fallback as Broker Intelligence Hub
          setBrokerageInfo({
            brokerageName: 'KairosIQ AI Realty, Inc.',
            brokerName: 'Michael Boykin',
            agentCount: 0,
            state: 'AZ',
            city: 'Chandler'
          });
        }
      } catch (profileError) {
        console.error('🔍 [Dashboard] Error fetching company profile:', profileError);
        
        // Fallback: Use the exact same default as Broker Intelligence Hub
        console.log('🔍 [Dashboard] Using default brokerage data...');
        setBrokerageInfo({
          brokerageName: 'KairosIQ AI Realty, Inc.',
          brokerName: 'Michael Boykin',
          agentCount: 0,
          state: 'AZ',
          city: 'Chandler'
        });
      }

      // Set agent info from localStorage user
      console.log('🔍 [Dashboard] Setting agent info from user:', {
        hasFirstName: !!user.user_first_name,
        hasLastName: !!user.user_last_name,
        firstName: user.user_first_name,
        lastName: user.user_last_name,
        licenseNumber: user.license_number,
        licenseState: user.license_state,
        fullUser: user
      });
      
      const agentName = user.user_first_name && user.user_last_name 
        ? `${user.user_first_name} ${user.user_last_name}`.trim()
        : user.user_first_name || user.user_last_name || user.name || 'Real Estate Agent';
      
      // Format license number properly
      const licenseDisplay = user.license_number && user.license_state
        ? `${user.license_state}-${user.license_number}`
        : user.license_number || 'Contact Admin for License Info';
        
      setAgentInfo({
        name: agentName,
        title: user.title || 'Licensed Real Estate Agent',
        licenseNumber: licenseDisplay,
        joinDate: user.created_at || new Date().toISOString(),
        rank: 'Top Performer',
        totalTransactions: 0 // Will be updated from metrics
      });
      
      console.log('🔍 [Dashboard] Agent info set:', {
        name: agentName,
        license: licenseDisplay
      });
      
      // If license is missing, try to fetch from agent profile
      if (!user.license_number) {
        console.log('🔍 [Dashboard] License not in localStorage, fetching agent profile...');
        try {
          const profileResponse = await axiosInstance.get('/agent/profile');
          if (profileResponse.data?.license_number) {
            const licenseFromProfile = profileResponse.data.license_state 
              ? `${profileResponse.data.license_state}-${profileResponse.data.license_number}`
              : profileResponse.data.license_number;
            
            setAgentInfo(prev => ({
              ...prev,
              licenseNumber: licenseFromProfile
            }));
            
            console.log('🔍 [Dashboard] License updated from profile:', licenseFromProfile);
          }
        } catch (profileError) {
          console.log('🔍 [Dashboard] Could not fetch agent profile:', profileError);
        }
      }

      // Fetch real transaction metrics from the existing API
      console.log('🔍 [Dashboard] Fetching real transaction metrics...');
      try {
        const transactionMetricsResponse = await axiosInstance.get('/transactions/metrics');
        console.log('🔍 [Dashboard] Transaction metrics response:', transactionMetricsResponse.data);
        
        if (transactionMetricsResponse.data?.success) {
          const metrics = transactionMetricsResponse.data;
          
          // Map the transaction metrics to agent stats
          setAgentStats({
            activeTransactions: metrics.active || 0,
            closedThisMonth: metrics.closedThisMonth || 0,
            pipelineValue: metrics.totalPipelineValue || 0,
            commissionEarned: metrics.totalCommissionEarned || 0,
            averageDaysToClose: metrics.averageDaysToClose || 0,
            goalProgress: metrics.goalProgress || 0,
            monthlyGoal: metrics.monthlyGoal || 5
          });
          
          // Update agent info with total active transactions
          setAgentInfo(prev => ({
            ...prev,
            totalTransactions: metrics.active || 0
          }));
          
          console.log('🔍 [Dashboard] Real transaction metrics loaded:', {
            active: metrics.active,
            closed: metrics.closedThisMonth,
            pipeline: metrics.totalPipelineValue
          });
        } else {
          console.warn('🔍 [Dashboard] Transaction metrics not successful');
        }
      } catch (statsError) {
        console.log('📊 [Dashboard] Transaction metrics API error:', statsError);
        console.log('📊 [Dashboard] Using fallback transaction stats');
        
        // Use more realistic fallback based on an active agent
        setAgentStats({
          activeTransactions: 3,
          closedThisMonth: 2,
          pipelineValue: 850000,
          commissionEarned: 12500,
          averageDaysToClose: 35,
          goalProgress: 67,
          monthlyGoal: 3
        });
      }

      // Fetch ALL transactions for Quick File View
      console.log('🔍 [Dashboard] Fetching all transactions...');
      try {
        const transactionsResponse = await axiosInstance.get('/transactions');
        console.log('🔍 [Dashboard] All transactions response:', transactionsResponse.data);
        
        if (transactionsResponse.data?.transactions) {
          // Filter to only show active/pending transactions (not completed or cancelled)
          const activeFiles = transactionsResponse.data.transactions
            .filter((txn: any) => 
              txn.stage !== 'completed' && 
              txn.stage !== 'cancelled' && 
              txn.stage !== 'closed'
            )
            .map((txn: any) => ({
              id: txn.id,
              property_address: txn.property_address || `${txn.client_name || 'Client'}'s Transaction`,
              stage: txn.stage || 'active',
              days_in_stage: Math.floor((Date.now() - new Date(txn.updated_at || txn.created_at).getTime()) / (1000 * 60 * 60 * 24)),
              next_action: txn.next_action || 'Review documents',
              client_name: txn.client_name || 'Unknown Client',
              type: txn.transaction_type || 'buyer'
            }));
          setActiveFiles(activeFiles);
          console.log('🔍 [Dashboard] Active files loaded:', activeFiles.length, 'out of', transactionsResponse.data.transactions.length, 'total');
        }
      } catch (filesError) {
        console.log('📁 [Dashboard] Active transactions API error:', filesError);
      }

      // Fetch ALL recent broker review items
      console.log('🔍 [Dashboard] Fetching all broker review items...');
      try {
        // Get all transactions to check their documents
        const txnResponse = await axiosInstance.get('/transactions');
        if (txnResponse.data?.transactions) {
          const allReviews: BrokerReviewItem[] = [];
          
          // Check more transactions (up to 10 most recent)
          const recentTransactions = txnResponse.data.transactions.slice(0, 10);
          
          for (const txn of recentTransactions) {
            try {
              const reviewResponse = await axiosInstance.get(`/broker-review/documents/${txn.id}`);
              console.log(`📄 [Dashboard] Documents for transaction ${txn.id}:`, reviewResponse.data?.documents);
              
              if (reviewResponse.data?.documents) {
                const reviews = reviewResponse.data.documents
                  .filter((doc: any) => doc.broker_review_status) // Get ALL statuses
                  .map((doc: any) => {
                    console.log(`📄 [Dashboard] Document status mapping:`, {
                      name: doc.document_name,
                      raw_status: doc.broker_review_status,
                      score: doc.broker_compliance_score || doc.compliance_score
                    });
                    
                    // Map the status - check for different possible values
                    let mappedStatus = 'pending';
                    if (doc.broker_review_status === 'approved' || 
                        doc.broker_review_status === 'approve' || 
                        doc.broker_review_status === 'passed') {
                      mappedStatus = 'approved';
                    } else if (doc.broker_review_status === 'needs_revision' || 
                               doc.broker_review_status === 'revision' ||
                               doc.broker_review_status === 'rejected') {
                      mappedStatus = 'needs_revision';
                    }
                    
                    return {
                      id: doc.id,
                      transaction_id: txn.id,
                      document_name: doc.document_name,
                      status: mappedStatus,
                      submitted_date: doc.uploaded_at || doc.added_at,
                      reviewed_date: doc.broker_reviewed_at,
                      score: doc.broker_compliance_score || doc.compliance_score,
                      broker_notes: doc.broker_review_notes || doc.broker_notes,
                      property_address: txn.property_address || `${txn.client_name || 'Client'}'s Transaction`
                    };
                  });
                allReviews.push(...reviews);
              }
            } catch (docError) {
              console.log(`📄 [Dashboard] Could not fetch documents for transaction ${txn.id}`);
            }
          }
          
          // Sort by most recent activity (reviewed date or submitted date)
          allReviews.sort((a, b) => {
            const dateA = new Date(a.reviewed_date || a.submitted_date).getTime();
            const dateB = new Date(b.reviewed_date || b.submitted_date).getTime();
            return dateB - dateA; // Most recent first
          });
          
          // Limit to 20 most recent reviews
          const recentReviews = allReviews.slice(0, 20);
          
          setBrokerReviews(recentReviews);
          console.log('🔍 [Dashboard] Broker review items loaded:', recentReviews.length, 'total');
        }
      } catch (reviewError) {
        console.log('📋 [Dashboard] Broker review API error:', reviewError);
      }

      // Try to fetch recent activity
      try {
        const activityResponse = await axiosInstance.get('/agent/recent-activity');
        if (activityResponse.data?.success) {
          setRecentActivity(activityResponse.data.activities);
        }
      } catch (activityError) {
        console.log('📋 [Dashboard] Activity API not available, using defaults');
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
      
      // Set fallback data with realistic numbers for an active agent
      console.warn('🔄 Using fallback data due to API errors');
      setAgentStats({
        activeTransactions: 2,
        closedThisMonth: 1,
        pipelineValue: 650000,
        commissionEarned: 8500,
        averageDaysToClose: 42,
        goalProgress: 50,
        monthlyGoal: 2
      });
      
      // Brokerage info will be set by company profile above
      // Agent info will be set by user data above

      setRecentActivity([
        {
          id: '1',
          type: 'document',
          title: 'Contract Signed',
          description: 'Purchase agreement signed for 123 Main St',
          timestamp: '2 hours ago',
          priority: 'high'
        },
        {
          id: '2',
          type: 'client',
          title: 'New Client Added',
          description: 'Sarah Johnson - First-time homebuyer',
          timestamp: '4 hours ago',
          priority: 'medium'
        },
        {
          id: '3',
          type: 'closing',
          title: 'Closing Scheduled',
          description: '456 Oak Ave - Closing set for Friday 2 PM',
          timestamp: '1 day ago',
          priority: 'high'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'document': return <FaHandshake className="text-primary" />;
      case 'client': return <FaUsers className="text-info" />;
      case 'transaction': return <FaDollarSign className="text-success" />;
      case 'closing': return <FaCalendarCheck className="text-warning" />;
      default: return <FaBell />;
    }
  };

  const getPriorityBadge = (priority?: string) => {
    switch (priority) {
      case 'high': return <Badge bg="danger" className="ms-2">High</Badge>;
      case 'medium': return <Badge bg="warning" className="ms-2">Medium</Badge>;
      case 'low': return <Badge bg="secondary" className="ms-2">Low</Badge>;
      default: return null;
    }
  };

  // Quick File View Component
  const QuickFileView: React.FC = () => {
    if (activeFiles.length === 0) {
      return (
        <Card 
          className="h-100 border-0"
          style={{
            background: 'rgba(59, 130, 246, 0.05)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '16px'
          }}
        >
          <Card.Header className="border-bottom-0 py-3" style={{ background: 'transparent' }}>
            <h5 className="mb-0 fw-semibold">📁 Active Files</h5>
          </Card.Header>
          <Card.Body className="text-center py-5">
            <p className="text-muted mb-0">No active transactions at the moment</p>
            <Button variant="primary" size="sm" className="mt-3" onClick={() => navigate('/transactions')}>
              Start New Transaction
            </Button>
          </Card.Body>
        </Card>
      );
    }

    return (
      <Card 
        className="h-100 border-0"
        style={{
          background: 'rgba(59, 130, 246, 0.05)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          borderRadius: '16px'
        }}
      >
        <Card.Header className="border-bottom-0 py-3" style={{ background: 'transparent' }}>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0 fw-semibold">📁 Active Files ({activeFiles.length})</h5>
            <Button variant="link" size="sm" onClick={() => navigate('/transactions')}>
              View All
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <div 
            className="active-files-scroll px-3 py-2" 
            style={{ 
              maxHeight: '400px', 
              overflowY: 'auto',
              overflowX: 'hidden'
            }}
          >
            {activeFiles.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted mb-2">No active files at the moment</p>
                <Button 
                  variant="primary" 
                  size="sm" 
                  onClick={() => navigate('/transactions')}
                >
                  Start New Transaction
                </Button>
              </div>
            ) : (
              activeFiles.map((file, index) => (
                <div 
                  key={file.id} 
                  className={`py-3 px-2 rounded hover-bg ${index !== activeFiles.length - 1 ? 'border-bottom' : ''}`}
                  style={{ 
                    borderColor: 'rgba(59, 130, 246, 0.1) !important', 
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onClick={() => navigate(`/agent-portal/transactions/${file.id}/documents`)}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <h6 className="mb-1" style={{ fontSize: '14px', fontWeight: '600' }}>
                      {file.property_address}
                    </h6>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <Badge 
                        bg={file.stage === 'under_contract' ? 'success' : file.stage === 'pending' ? 'warning' : 'primary'}
                        style={{ fontSize: '11px' }}
                      >
                        {file.stage.replace(/_/g, ' ').toUpperCase()}
                      </Badge>
                      <small className="text-muted">Day {file.days_in_stage}</small>
                      <small className="text-muted">•</small>
                      <small className="text-muted">{file.client_name}</small>
                    </div>
                    <small className="text-primary">
                      <strong>Next:</strong> {file.next_action}
                    </small>
                  </div>
                  <FaChartLine className="text-muted" style={{ fontSize: '16px' }} />
                </div>
              </div>
              ))
            )}
          </div>
        </Card.Body>
      </Card>
    );
  };

  // Broker Review Status Component
  const BrokerReviewStatus: React.FC = () => {
    const pendingCount = brokerReviews.filter(r => r.status === 'pending').length;
    const revisionCount = brokerReviews.filter(r => r.status === 'needs_revision').length;
    const approvedCount = brokerReviews.filter(r => r.status === 'approved').length;

    if (brokerReviews.length === 0) {
      return (
        <Card 
          className="h-100 border-0"
          style={{
            background: 'rgba(245, 158, 11, 0.05)',
            border: '1px solid rgba(245, 158, 11, 0.2)',
            borderRadius: '16px'
          }}
        >
          <Card.Header className="border-bottom-0 py-3" style={{ background: 'transparent' }}>
            <h5 className="mb-0 fw-semibold">📋 Recent Broker Reviews</h5>
          </Card.Header>
          <Card.Body className="text-center py-5">
            <p className="text-muted mb-0">No recent document reviews</p>
          </Card.Body>
        </Card>
      );
    }

    return (
      <Card 
        className="h-100 border-0"
        style={{
          background: 'rgba(245, 158, 11, 0.05)',
          border: '1px solid rgba(245, 158, 11, 0.2)',
          borderRadius: '16px'
        }}
      >
        <Card.Header className="border-bottom-0 py-3" style={{ background: 'transparent' }}>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0 fw-semibold">📋 Recent Broker Reviews</h5>
            <div className="d-flex gap-2">
              {approvedCount > 0 && <Badge bg="success">{approvedCount} ✓</Badge>}
              {pendingCount > 0 && <Badge bg="warning">{pendingCount} ⏳</Badge>}
              {revisionCount > 0 && <Badge bg="danger">{revisionCount} ⚠️</Badge>}
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <div 
            className="broker-reviews-scroll px-3 py-2" 
            style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {brokerReviews.map((review, index) => (
              <div 
                key={review.id} 
                className={`py-3 px-2 rounded ${index !== brokerReviews.length - 1 ? 'border-bottom' : ''}`}
                style={{ 
                  borderColor: 'rgba(245, 158, 11, 0.1) !important', 
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onClick={() => navigate(`/agent-portal/transactions/${review.transaction_id}/documents`)}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(245, 158, 11, 0.05)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <h6 className="mb-1" style={{ fontSize: '14px', fontWeight: '600' }}>
                      {review.document_name}
                    </h6>
                    <small className="text-muted d-block mb-1">{review.property_address}</small>
                    <div className="d-flex align-items-center gap-2">
                      <Badge 
                        bg={review.status === 'approved' ? 'success' : review.status === 'needs_revision' ? 'danger' : 'warning'}
                        style={{ fontSize: '11px' }}
                      >
                        {review.status === 'approved' ? '✅ Approved' : 
                         review.status === 'needs_revision' ? '⚠️ Revision Needed' : 
                         '⏳ Pending Review'}
                      </Badge>
                      {review.score && (
                        <small className="text-muted">Score: {review.score}%</small>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2 mt-1">
                      <small className="text-muted">
                        {review.reviewed_date ? 
                          `Reviewed ${formatTimestamp(review.reviewed_date)}` : 
                          `Submitted ${formatTimestamp(review.submitted_date)}`}
                      </small>
                    </div>
                    {review.broker_notes && review.status !== 'approved' && (
                      <small className="text-danger d-block mt-1">
                        <strong>Note:</strong> {review.broker_notes}
                      </small>
                    )}
                  </div>
                  {review.status === 'approved' ? (
                    <FaCheckCircle className="text-success" style={{ fontSize: '16px' }} />
                  ) : review.status === 'needs_revision' ? (
                    <FaExclamationTriangle className="text-danger" style={{ fontSize: '16px' }} />
                  ) : (
                    <FaClock className="text-warning" style={{ fontSize: '16px' }} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>
    );
  };

  return (
    <>
      <style>
        {`
          .active-files-scroll::-webkit-scrollbar {
            width: 6px;
          }
          .active-files-scroll::-webkit-scrollbar-track {
            background: rgba(59, 130, 246, 0.05);
            border-radius: 3px;
          }
          .active-files-scroll::-webkit-scrollbar-thumb {
            background: rgba(59, 130, 246, 0.3);
            border-radius: 3px;
          }
          .active-files-scroll::-webkit-scrollbar-thumb:hover {
            background: rgba(59, 130, 246, 0.5);
          }
          .file-item-hover:hover {
            background-color: rgba(59, 130, 246, 0.05);
            transform: translateX(2px);
            transition: all 0.2s ease;
          }
          .broker-reviews-scroll::-webkit-scrollbar {
            width: 6px;
          }
          .broker-reviews-scroll::-webkit-scrollbar-track {
            background: rgba(245, 158, 11, 0.05);
            border-radius: 3px;
          }
          .broker-reviews-scroll::-webkit-scrollbar-thumb {
            background: rgba(245, 158, 11, 0.3);
            border-radius: 3px;
          }
          .broker-reviews-scroll::-webkit-scrollbar-thumb:hover {
            background: rgba(245, 158, 11, 0.5);
          }
        `}
      </style>
      <Container fluid className="py-3">
      {/* Enhanced Header with Agent & Brokerage Info */}
      <div 
        className="dashboard-header mb-4"
        style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Row className="align-items-center">
          <Col md={8}>
            <div className="d-flex align-items-center gap-3">
              <div 
                style={{
                  width: '60px',
                  height: '60px',
                  background: 'linear-gradient(135deg, #00d4ff 0%, #667eea 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 20px rgba(0, 212, 255, 0.3)',
                }}
              >
                <FaUserTie style={{ fontSize: '24px', color: 'white' }} />
              </div>
              <div>
                <h1 className="mb-1" style={{ fontSize: '1.8rem', fontWeight: '600', color: '#1f2937' }}>
                  Welcome back, {agentInfo.name}
                </h1>
                <div className="d-flex align-items-center gap-3 mb-2">
                  <Badge bg="primary" style={{ fontSize: '12px' }}>{agentInfo.title}</Badge>
                  <Badge bg="success" style={{ fontSize: '12px' }}>
                    <FaStar className="me-1" />
                    {agentInfo.rank}
                  </Badge>
                  <span className="text-muted" style={{ fontSize: '14px' }}>
                    License: {agentInfo.licenseNumber}
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <FaBuilding className="text-muted" />
                  <strong>{brokerageInfo.brokerageName}</strong>
                  <span className="text-muted">•</span>
                  <span className="text-muted">Broker: {brokerageInfo.brokerName}</span>
                  <span className="text-muted">•</span>
                  <FaMapMarkerAlt className="text-muted" />
                  <span className="text-muted">{brokerageInfo.city}, {brokerageInfo.state}</span>
                </div>
              </div>
            </div>
          </Col>
          <Col md={4}>
            <div className="text-end">
              <div className="d-flex gap-2 justify-content-end mb-3">
                <Button variant="primary" size="sm" onClick={() => navigate('/agent-portal/transactions/new')}>
                  <FaPlus className="me-1" /> New Transaction
                </Button>
                <Button variant="outline-primary" size="sm" onClick={() => navigate('/agent-portal/clients/new')}>
                  <FaUsers className="me-1" /> Add Client
                </Button>
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                Team of {brokerageInfo.agentCount} agents • {agentInfo.totalTransactions} total transactions
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* KPI Cards Row */}
      <Row className="mb-4">
        <Col md={2}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center py-4">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FaHandshake style={{ color: 'white', fontSize: '18px' }} />
                </div>
              </div>
              <h3 className="mb-1" style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                {agentStats.activeTransactions}
              </h3>
              <p className="text-muted mb-0" style={{ fontSize: '12px' }}>Active Transactions</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center py-4">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FaCalendarCheck style={{ color: 'white', fontSize: '18px' }} />
                </div>
              </div>
              <h3 className="mb-1" style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                {agentStats.closedThisMonth}
              </h3>
              <p className="text-muted mb-0" style={{ fontSize: '12px' }}>Closed This Month</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center py-4">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FaDollarSign style={{ color: 'white', fontSize: '18px' }} />
                </div>
              </div>
              <h3 className="mb-1" style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937' }}>
                {formatCurrency(agentStats.pipelineValue)}
              </h3>
              <p className="text-muted mb-0" style={{ fontSize: '12px' }}>Pipeline Value</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center py-4">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FaTrophy style={{ color: 'white', fontSize: '18px' }} />
                </div>
              </div>
              <h3 className="mb-1" style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937' }}>
                {formatCurrency(agentStats.commissionEarned)}
              </h3>
              <p className="text-muted mb-0" style={{ fontSize: '12px' }}>Commission Earned</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center py-4">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FaChartLine style={{ color: 'white', fontSize: '18px' }} />
                </div>
              </div>
              <h3 className="mb-1" style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                {agentStats.averageDaysToClose}
              </h3>
              <p className="text-muted mb-0" style={{ fontSize: '12px' }}>Avg Days to Close</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center py-4">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FaEye style={{ color: 'white', fontSize: '18px' }} />
                </div>
              </div>
              <div className="mb-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>Monthly Goal</span>
                  <strong style={{ fontSize: '14px', color: '#1f2937' }}>{agentStats.goalProgress}%</strong>
                </div>
                <ProgressBar 
                  now={agentStats.goalProgress} 
                  style={{ height: '6px' }}
                  variant={agentStats.goalProgress > 80 ? 'success' : agentStats.goalProgress > 50 ? 'warning' : 'danger'}
                />
              </div>
              <p className="text-muted mb-0" style={{ fontSize: '11px' }}>
                {agentStats.closedThisMonth} of {agentStats.monthlyGoal} closes
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Quick File View and Broker Review Status */}
      <Row className="mb-4">
        <Col md={6}>
          <QuickFileView />
        </Col>
        <Col md={6}>
          <BrokerReviewStatus />
        </Col>
      </Row>
    </Container>
    </>
  );
};

export default EnhancedAgentDashboard;